import React, { useEffect, useState } from "react";
import styles from "./institutions.module.css";
import { Popconfirm, Typography } from "antd";
import CustomButton from "../../common/custom-button/custom-button";
import SVGIcon from "../../constants/SVGIcon";
import CustomTextfield from "../../common/input/custom-textfield/custom-textfield";
import CustomFilter from "../../components/custom-filter/custom-filter";
import { deleteInstitution, getInstitutions } from "../../services/institution";
import CustomTable from "../../common/custom-table/custom-table";
import CustomProfileAvatar from "../../common/custom-profile-avatar/custom-profile-avatar";
import RoutesConstant from "../../constants/routes";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomTag from "../../common/custom-tag/custom-tag";
import {
  curriculumType,
  ownershipStatusType,
  schoolType,
} from "../../constants/common";
import dayjs from "dayjs";

const Institutions = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [institutionList, setInstitutionList] = useState([]);
  const [filterValues, setFilterValues] = useState({
    curriculum: [],
    school_type: [],
  });

  const filterData = [
    {
      id: "curriculum",
      label: "Curriculum",
      data: curriculumType?.map((item) => item?.value),
    },
    {
      id: "school_type",
      label: "School Type",
      data: schoolType?.map((item) => item?.value),
    },
    {
      id: "ownership_type",
      label: "Ownership Type",
      data: ownershipStatusType,
    },
  ];

  const columns = [
    {
      title: "Institution",
      dataIndex: "institution_name",
      key: "institution_name",
      render: (value, record) => (
        <div
          className={
            record?.logo
              ? styles.nameContainer
              : styles.nameContainerWithoutLogo
          }
        >
          <CustomProfileAvatar
            image={
              record?.institution_logo
                ? record?.institution_logo
                : "sample-school.jpg"
            }
            imageName={record?.institution_name}
            imageSrc={
              record?.institution_logo ?? "assets/images/sample-school.jpg"
            }
            divBackgroundColor={"#97a997"}
          />
          <Typography>{value}</Typography>
        </div>
      ),
    },
    {
      title: "Province",
      dataIndex: "province",
      key: "province",
      render: (value) => value || "-",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (value) =>
        value ? (
          <CustomTag
            text={value}
            color={"#39b54a"}
            background={"rgba(57, 181, 74, 0.1)"}
            borderColor={"#39b54a"}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Reg. No.",
      dataIndex: "registration_number",
      key: "registration_number",
      render: (value) => value || "-",
    },
    {
      title: "Phone No.",
      dataIndex: "school_phone_number",
      key: "school_phone_number",
      render: (value) => value || "-",
    },
    {
      title: "Email",
      dataIndex: "school_email",
      key: "school_email",
      render: (value) => value || "-",
    },
    {
      title: "Onboarded On",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => dayjs(value).format("MM-DD-YYYY") || "-",
    },
  ];

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () =>
        navigate(`${RoutesConstant.INSTITUTION_DETAILS}?uid=${record?.id}`),
    },
    {
      key: "2",
      label: "Update",
      onClick: () => onUpdateDetails(record),
    },
    {
      key: "3",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.institution_name}?`}
          onConfirm={() => handleDeleteInstitution(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setPageNo(1);
      getInstitutionList(1);
    }, 500);
    return () => {
      clearTimeout(timeOut);
    };
  }, [searchTerm, filterValues]);

  const handleDeleteInstitution = async (institution_id) => {
    try {
      setLoading(true);
      const response = await deleteInstitution(institution_id);
      if (response?.status) {
        toast.success("Institution Deleted Successfully !");
        getInstitutionList();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.failure("An error occured, please try again.");
      console.log("error", error);
    }
  };

  const getInstitutionList = async (
    tempPage = pageNo,
    tempPageSize = pageSize
  ) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
      };

      if (searchTerm) {
        payload.search = searchTerm;
      }
      Object.entries(filterValues).forEach(([key, valueArray]) => {
        if (Array.isArray(valueArray) && valueArray.length > 0) {
          payload[key] = valueArray[0];
        }
      });

      const response = await getInstitutions(payload);
      if (response?.data) {
        const tempList = response?.data?.data?.schools?.map((e, i) => {
          return {
            index: i + 1,
            ...e,
          };
        });
        setInstitutionList(tempList);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const onChangeFilter = (id, values) => {
    const existingValues = filterValues[id] || [];
    const removedValue = existingValues.find((val) => !values.includes(val));
    const addedValue = values.find((val) => !existingValues.includes(val));

    if (removedValue) {
      const updatedValues = existingValues.filter(
        (val) => val !== removedValue
      );
      setFilterValues((prev) => ({
        ...prev,
        [id]: updatedValues,
      }));
    } else if (addedValue) {
      setFilterValues((prev) => ({
        ...prev,
        [id]: [addedValue],
      }));
    }
  };

  const onFilterReset = () => {
    setFilterValues({
      status: [],
    });
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getInstitutionList(page, pageSize);
  };

  const handleAddInstitutions = () => {
    navigate(RoutesConstant.INSTITUTION_FORM);
  };

  const onUpdateDetails = (record) => {
    navigate(`${RoutesConstant.INSTITUTION_FORM}?uid=${record?.id}`);
  };

  return (
    <div className={styles.institutions}>
      <div className={styles.headerContainer}>
        <Typography className={styles.title}>Institutions</Typography>
        <div className={styles.filterContainer}>
          <CustomTextfield
            id={"search"}
            type={"text"}
            className={styles.inputField}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            prefix={
              <SVGIcon
                name={"search-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#667085"}
              />
            }
            placeholder={"Search"}
            allowClear
          />
          <CustomFilter
            height="36px"
            width={styles.filter}
            filterData={filterData}
            values={filterValues}
            onResetFilter={onFilterReset}
            onChange={(id, values) => onChangeFilter(id, values)}
          />
          <CustomButton
            title="Add New Institutions"
            size={"large"}
            icon={
              <SVGIcon
                name={"add-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#FFF"}
              />
            }
            onClick={handleAddInstitutions}
          />
        </div>
      </div>

      <div className={styles.tableContainer}>
        <CustomTable
          columns={columns}
          dataSource={institutionList}
          total={totalCount}
          loading={loading}
          showMoreIcon={true}
          menuItems={menuItems}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>
    </div>
  );
};

export default Institutions;
